// ============================================================================
// Star rating view
// ============================================================================

import { SocketClient } from "cbcore-ts"
import { IF, NO, UIColor, UICore, UIRoute, UIRootViewController, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { titleKeyOfViewController } from "./Core/RHViewControllerTitleKey"
import { CBDialogViewShower } from "./Custom components/CB/CBDialogViewShower"
import { RHFocusManager } from "./Custom components/RH/Keyboard navigation/RHFocusManager"
import { RHPageFocusManager } from "./Custom components/RH/Keyboard navigation/RHPageFocusManager"
import { RHButton } from "./Custom components/RH/RHButton"
import { RHColor } from "./Custom components/RH/RHColor"
import { RHFileUploadView } from "./Custom components/RH/RHFileUploadView"
import { RHTextArea } from "./Custom components/RH/RHTextArea"
import { RHButtons, RHColors, RHFont, RHRadius, RHSpacing } from "./Custom components/RH/Utils_slop/RHDesignTokens"
import { RHFeedbackViewTokens } from "./Custom components/RH/Utils_slop/RHViewTokens"

class FeedbackStarRatingView extends UIView {
    
    readonly _stars: RHButton[] = []
    
    _rating = 0
    _hoverRating = 0
    
    ratingDidChange: (rating: number) => void = () => {
    }
    
    constructor(elementID?: string) {
        super(elementID)
        for (let i = 1; i <= 5; i++) {
            const index = i
            const star = new RHButton()
                .configuredWithObject({
                    titleLabel: {
                        text: "★",
                        style: {
                            fontSize: `${RHSpacing.xLarge}px`,
                            lineHeight: "1"
                        }
                    },
                    variant: "link",
                    contentPadding: 0,
                    style: {
                        padding: "0px",
                        border: "none",
                        boxShadow: "none",
                        backgroundImage: "none",
                        lineHeight: "1"
                    },
                    controlEventTargetAccumulator: {
                        PointerHover: () => {
                            this._hoverRating = index
                            this._updateStarColors()
                        },
                        PointerEnter: () => {
                            this._hoverRating = index
                            this._updateStarColors()
                        },
                        PointerLeave: () => {
                            this._hoverRating = 0
                            this._updateStarColors()
                        },
                        PointerUpInside: () => {
                            this.rating = index
                        }
                    }
                })
                .addedAsSubviewToView(this)
            this._stars.push(star)
        }
        this._updateStarColors()
    }
    
    get rating(): number {
        return this._rating
    }
    
    set rating(value: number) {
        this._rating = value
        this._hoverRating = 0
        this._updateStarColors()
        this.ratingDidChange(value)
    }
    
    private _updateStarColors() {
        const visibleRating = IF(this._hoverRating)(() =>
            this._hoverRating
        ).ELSE(() =>
            this._rating
        )
        for (let i = 0; i < this._stars.length; i++) {
            const isFilled = i < visibleRating
            const starColor = IF(isFilled)(() =>
                RHColors.ratingGold
            ).ELSE(() =>
                RHColors.textPlaceholder
            )
            this._stars[i].colors.titleLabel.normal = starColor
            this._stars[i].colors.titleLabel.hovered = starColor
            this._stars[i].colors.titleLabel.highlighted = starColor
            this._stars[i].colors.titleLabel.focused = starColor
            this._stars[i].colors.titleLabel.selected = starColor
            this._stars[i].titleLabel.textColor = starColor
            this._stars[i].updateContentForCurrentState()
        }
    }
    
    override layoutSubviews() {
        super.layoutSubviews()
        const starRowWidth =
            this._stars.length * RHFeedbackViewTokens.ratingStarButtonSize +
            (this._stars.length - 1) * RHSpacing.tiny
        const starRowFrame = this.contentBoundsWithInset(0)
            .rectangleWithWidth(starRowWidth, 0.5)
            .rectangleWithHeight(RHFeedbackViewTokens.ratingRowHeight, 0.5)
        starRowFrame.distributeViewsAlongWidth(
            this._stars,
            undefined,
            RHSpacing.tiny,
            this._stars.map(() => RHFeedbackViewTokens.ratingStarButtonSize)
        )
    }
    
    override intrinsicContentHeight(constrainingWidth?: number): number {
        return RHFeedbackViewTokens.ratingRowHeight
    }
    
}


// ============================================================================
// Feedback view controller
// ============================================================================

export class FeedbackViewController extends UIViewController {
    
    static override readonly routeComponentName = "feedback"
    
    readonly titleLabel = new UITextView(undefined, UITextView.type.header4)
        .configuredWithObject({
            text: "Anna tagasisidet",
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeXXLarge,
                fontWeight: "500",
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly explanationLabel = new UITextView()
        .configuredWithObject({
            text: "Sinu arvamus aitab meil rakendust paremaks muuta. Tagasiside edastatakse arendusmeeskonnale.",
            isSingleLine: NO,
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: "400",
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly activeViewLabel = new UITextView()
        .configuredWithObject({
            hidden: YES,
            textColor: RHColor.textMuted,
            style: { fontStyle: "italic" }
        })
        .addedAsSubviewToView(this.view)
    
    readonly ratingTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Hinda oma kogemust (valikuline):",
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFeedbackViewTokens.labelFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly starRatingView = new FeedbackStarRatingView()
        .addedAsSubviewToView(this.view)
    
    readonly messageTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Tagasiside:",
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFeedbackViewTokens.labelFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly messageTextArea = new RHTextArea()
        .configuredWithObject({
            placeholderText: "Kirjelda oma kogemust, probleemi või ettepanekut…"
        })
        .addedAsSubviewToView(this.view)
    
    readonly attachmentTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Manus (valikuline):",
            textColor: RHColors.textPrimary,
            style: {
                fontFamily: RHFont.familyBase,
                fontSize: RHFont.sizeMedium,
                fontWeight: RHFeedbackViewTokens.labelFontWeight,
                lineHeight: RHFont.lineHeightHeading
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly attachmentUploadView = new RHFileUploadView()
        .configuredWithObject({ bulkDownloadEnabled: NO })
        .addedAsSubviewToView(this.view)
    
    readonly submitButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Saada tagasiside" },
            variant: "success",
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: async () => {
                    await this._submitFeedback()
                }
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Tühista" },
            variant: RHButtons.destructiveUtilityVariant,
            contentPadding: RHButtons.compactContentPadding,
            style: {
                fontSize: RHButtons.compactFontSize
            },
            controlEventTargetAccumulator: {
                PrimaryActionTriggered: () => {
                    this._dismiss()
                }
            }
        })
        .addedAsSubviewToView(this.view)
    
    override _focusManager = new RHFocusManager(() => [
        this.messageTextArea,
        this.attachmentUploadView.dropZone,
        this.submitButton,
        this.cancelButton
    ])
    
    _capturedPageUrl = ""
    _capturedViewControllerClassName = ""
    _capturedViewControllerRouteComponentName = ""
    _capturedViewControllerTitleKey = ""
    
    _textAreaHasFocus = NO
    
    readonly _escKeyHandler = (event: KeyboardEvent) => {
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Escape") {
            if (!this._hasDraftContent()) {
                this._dismiss()
            }
            else if (!this._textAreaHasFocus) {
                const shower = CBDialogViewShower.showQuestionDialog(
                    "Sulge tagasiside aken? Sisestatud tekst läheb kaotsi."
                )
                shower.yesButtonWasPressed = () => {
                    shower.dialogView.dismiss()
                    this._dismiss()
                }
            }
        }
    }
    
    readonly _cmdEnterKeyHandler = (event: KeyboardEvent) => {
        if (CBDialogViewShower.currentDialogViewShower) {
            return
        }
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            this._submitFeedback()
        }
    }
    
    
    constructor() {
        super(new UIView())

        // UIRootViewController uses this value when sizing the details dialog;
        // without it, the narrow intrinsic width of individual controls wins.
        this.view.intrinsicContentWidth = () => Math.min(
            RHFeedbackViewTokens.dialogMaxWidth,
            UIView.pageWidth * RHFeedbackViewTokens.dialogWidthViewportRatio
        )
        
        this.view.configureWithObject({
            backgroundColor: UIColor.whiteColor,
            style: { borderRadius: `${RHRadius.xLarge}px` }
        })
        
        // noinspection JSConstantReassignment
        this.messageTextArea.controlEventTargetAccumulator.Focus = () => {
            this._textAreaHasFocus = YES
        }
        // noinspection JSConstantReassignment
        this.messageTextArea.controlEventTargetAccumulator.Blur = () => {
            this._textAreaHasFocus = NO
        }
    }
    
    
    override async viewWillAppear() {
        
        await super.viewWillAppear()
        
        // Capture context of the previously active content VC
        const rootViewController = UICore.main.rootViewController
        let activeViewController: UIViewController | undefined
        if (rootViewController instanceof UIRootViewController) {
            activeViewController = rootViewController.contentViewController
        }
        const vcClass: typeof UIViewController | undefined = activeViewController?.class
        this._capturedPageUrl = "" + window.location
        this._capturedViewControllerClassName = vcClass?.name ?? ""
        this._capturedViewControllerRouteComponentName = vcClass?.routeComponentName ?? ""
        this._capturedViewControllerTitleKey = titleKeyOfViewController(activeViewController) ?? ""
        
        this.activeViewLabel.text = ""
        this.activeViewLabel.hidden = YES
        
        // Reset form
        this.messageTextArea.text = ""
        this.starRatingView.rating = 0
        this.attachmentUploadView.clearFiles()
        
        document.body.addEventListener("keydown", this._escKeyHandler)
        document.body.addEventListener("keydown", this._cmdEnterKeyHandler)
        RHPageFocusManager.shared.pushManagerAsNewLayer(this._focusManager)
        
        this.messageTextArea.focus()
    }
    
    override async viewWillDisappear() {
        await super.viewWillDisappear()
        document.body.removeEventListener("keydown", this._escKeyHandler)
        document.body.removeEventListener("keydown", this._cmdEnterKeyHandler)
        RHPageFocusManager.shared.popLayer()
    }
    
    
    private _dismiss() {
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(FeedbackViewController.routeComponentName)
            .apply()
    }
    
    private async _submitFeedback() {
        const messageText = this.messageTextArea.text.trim()
        
        if (!messageText) {
            CBDialogViewShower.alert("Palun sisesta tagasiside tekst.")
            return
        }
        
        try {
            this.view.loading = YES
            
            const response = await SocketClient.SubmitFeedback({
                message: messageText,
                rating: this._optionalRating(),
                attachmentFileIds: this.attachmentUploadView.uploadedFiles.map(file => file.id),
                pageUrl: this._capturedPageUrl,
                activeViewControllerClassName: this._capturedViewControllerClassName,
                activeViewControllerRouteComponentName: this._capturedViewControllerRouteComponentName,
                activeViewControllerTitleKey: this._capturedViewControllerTitleKey
            })

            if (response.result?.success) {
                CBDialogViewShower.alert("Täname! Tagasiside on edukalt saadetud.")
                this._dismiss()
            }
            else {
                const serverErrorMessage = IF(
                    typeof response.errorResult === "string" && response.errorResult.length > 0
                )(() =>
                    response.errorResult as string
                ).ELSE(() =>
                    "Tagasiside saatmine ebaõnnestus. Palun proovi uuesti."
                )
                CBDialogViewShower.alert(serverErrorMessage)
            }
        }
        catch (error) {
            console.error("Error submitting feedback:", error)
            CBDialogViewShower.alert("Viga tagasiside saatmisel. Palun proovi uuesti.")
        }
        finally {
            this.view.loading = NO
        }
    }
    
    private _optionalRating(): number | undefined {
        return IF(this.starRatingView.rating > 0)(() =>
            this.starRatingView.rating
        ).ELSE(() =>
            undefined
        )
    }
    
    private _hasDraftContent() {
        return this.messageTextArea.text.trim().length > 0 ||
            this.attachmentUploadView.uploadedFiles.length > 0
    }
    
    
    override layoutViewSubviews() {
        
        this.view.frame = this.view.frame.rectangleWithMaxWidth(
            RHFeedbackViewTokens.dialogMaxWidth,
            0.5
        )

        const padding = this.core.paddingLength
        const bounds = this.view.contentBoundsWithInset(RHSpacing.large)
        const submitButtonWidth = this._buttonWidth(this.submitButton)
        const cancelButtonWidth = this._buttonWidth(this.cancelButton)
        const buttonRowWidth =
            submitButtonWidth +
            RHSpacing.medium +
            cancelButtonWidth
        
        this.titleLabel.frame = bounds.rectangleWithHeight(this.titleLabel)
        this.explanationLabel.frame = this.titleLabel.frame.rectangleForNextRow(
            padding * 0.5,
            this.explanationLabel
        )
        this.activeViewLabel.frame = this.explanationLabel.frame.rectangleForNextRow(0, 0)
        this.ratingTitleLabel.frame = this.explanationLabel.frame.rectangleForNextRow(
            padding,
            this.ratingTitleLabel
        )
        this.starRatingView.frame = this.ratingTitleLabel.frame.rectangleForNextRow(
            padding * 0.5,
            RHFeedbackViewTokens.ratingRowHeight
        )
        this.messageTitleLabel.frame = this.starRatingView.frame.rectangleForNextRow(
            padding,
            this.messageTitleLabel
        )
        this.messageTextArea.frame = this.messageTitleLabel.frame.rectangleForNextRow(
            padding * 0.5,
            180
        )
        this.attachmentTitleLabel.frame = this.messageTextArea.frame.rectangleForNextRow(
            padding,
            this.attachmentTitleLabel
        )
        this.attachmentUploadView.frame = this.attachmentTitleLabel.frame.rectangleForNextRow(
            padding * 0.5,
            this.attachmentUploadView
        )
        
        const buttonRowFrame = this.attachmentUploadView.frame
            .rectangleForNextRow(RHSpacing.large, RHButtons.compactHeight)
            .rectangleWithWidth(buttonRowWidth, 0.5)
        this.submitButton.frame = buttonRowFrame.rectangleWithWidth(submitButtonWidth)
        this.cancelButton.frame = this.submitButton.frame.rectangleForNextColumn(
            RHSpacing.medium,
            cancelButtonWidth
        )

        // The dialog grows to fit its content (the attachment list is variable
        // height) and stays centered in the viewport, capped so it never exceeds
        // the available height.
        const contentHeight = buttonRowFrame.max.y + RHSpacing.large
        const dialogBounds = this.view.superview?.bounds ?? this.view.frame
        const availableHeight = dialogBounds.height - RHSpacing.xLarge * 2
        this.view.frame = dialogBounds
            .rectangleWithWidth(this.view.frame.width, 0.5)
            .rectangleWithHeight([contentHeight, availableHeight].min(), 0.5)
    }
    
    private _buttonWidth(button: RHButton) {
        return [
            button.titleLabel.intrinsicContentWidth(RHButtons.compactHeight) +
            RHButtons.compactContentPadding * 2 +
            RHSpacing.medium,
            160
        ].max()
    }
    
}
